import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Zap, Copy, Check, Smartphone, Building2 } from "lucide-react";
import { toast } from "sonner";

// IBAN ufficiale Colucci Assicuratori - Christian e Ciro Colucci S.A.S.
const IBAN = "IT04D0313810000000012980744";
const INTESTATARIO = "CHRISTIAN E CIRO COLUCCI SAS";
const BIC = ""; // opzionale nello standard EPC

// Genera il payload EPC QR Code (standard EPC069-12)
// Formato riconosciuto da tutte le principali app bancarie europee
function buildEpcPayload({ importo, causale }) {
  const amount = Number(importo || 0).toFixed(2);
  return [
    "BCD",
    "002",
    "1",
    "SCT",
    BIC,
    INTESTATARIO,
    IBAN,
    `EUR${amount}`,
    "",
    "",
    causale || "",
  ].join("\n");
}

function formatIban(iban) {
  return iban.replace(/(.{4})/g, "$1 ").trim();
}

function CopyButton({ value, label = "Copia", testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copiato`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossibile copiare");
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md bg-[#163321]/10 hover:bg-[#163321]/20 text-[#163321] font-medium transition-colors"
      data-testid={testId}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copiato" : label}
    </button>
  );
}

export default function BonificoIstantaneoButton({
  importo,
  causale = "",
  variant = "primary",
  label = "Paga con Bonifico Istantaneo",
  className = "",
  testId = "bonifico-istantaneo-btn",
}) {
  const [open, setOpen] = useState(false);

  const epcPayload = buildEpcPayload({ importo, causale });
  const importoFormatted = new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(Number(importo || 0));

  const tryOpenBankApp = () => {
    // Schema sperimentale supportato da alcune app bancarie UE tramite URI SEPA
    // Se non supportato, il clic non fa nulla e resta la scansione QR
    const sepaUrl = `sepa://${IBAN}?amount=${Number(importo || 0).toFixed(2)}&currency=EUR&message=${encodeURIComponent(causale)}`;
    window.location.href = sepaUrl;
  };

  const btnStyle =
    variant === "primary"
      ? "bg-[#163321] hover:bg-[#1d4a2e] text-white"
      : "bg-white border border-[#163321] text-[#163321] hover:bg-[#163321]/5";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center justify-center gap-2 px-6 h-11 rounded-full font-semibold text-sm transition-all shadow-sm hover:shadow-md ${btnStyle} ${className}`}
        data-testid={testId}
      >
        <Zap className="w-4 h-4" /> {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="bonifico-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#163321]" /> Bonifico Istantaneo SEPA
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* BADGE ISTANTANEO */}
            <div className="bg-[#E8F0EA] border border-[#163321]/20 rounded-xl p-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#163321] shrink-0" />
              <p className="text-xs text-[#163321]">
                <strong>Accredito in 10 secondi</strong> — Commissioni azzerate rispetto alla carta di credito.
              </p>
            </div>

            {/* QR CODE */}
            <div className="bg-white border-2 border-[#163321]/10 rounded-2xl p-5 flex flex-col items-center">
              <QRCodeSVG
                value={epcPayload}
                size={200}
                level="M"
                bgColor="#FFFFFF"
                fgColor="#163321"
                data-testid="bonifico-qrcode"
              />
              <p className="text-xs text-[#4A5E51] mt-3 text-center flex items-center gap-1">
                <Smartphone className="w-3 h-3" /> Apri la tua app bancaria e inquadra il QR
              </p>
            </div>

            {/* IMPORTO */}
            {Number(importo) > 0 && (
              <div className="flex items-center justify-between bg-[#F5F8F6] rounded-xl p-3">
                <span className="text-xs text-[#4A5E51] uppercase tracking-wider font-medium">
                  Importo
                </span>
                <span className="font-heading text-lg font-bold text-[#163321]" data-testid="bonifico-importo">
                  {importoFormatted}
                </span>
              </div>
            )}

            {/* IBAN */}
            <div className="border border-[#E5E2DB] rounded-xl p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider text-[#4A5E51] font-medium">
                  IBAN
                </span>
                <CopyButton value={IBAN} label="Copia" testId="bonifico-copy-iban" />
              </div>
              <p className="font-mono text-sm text-[#0F1F14] tracking-wider" data-testid="bonifico-iban">
                {formatIban(IBAN)}
              </p>
            </div>

            {/* INTESTATARIO */}
            <div className="border border-[#E5E2DB] rounded-xl p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider text-[#4A5E51] font-medium">
                  Intestatario
                </span>
                <CopyButton value={INTESTATARIO} label="Copia" testId="bonifico-copy-intestatario" />
              </div>
              <p className="text-sm text-[#0F1F14] font-medium flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-[#4A5E51]" /> {INTESTATARIO}
              </p>
            </div>

            {/* CAUSALE */}
            {causale && (
              <div className="border border-[#E5E2DB] rounded-xl p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-wider text-[#4A5E51] font-medium">
                    Causale
                  </span>
                  <CopyButton value={causale} label="Copia" testId="bonifico-copy-causale" />
                </div>
                <p className="text-sm text-[#0F1F14]" data-testid="bonifico-causale">
                  {causale}
                </p>
              </div>
            )}

            {/* BOTTONE MOBILE */}
            <div className="sm:hidden">
              <Button
                onClick={tryOpenBankApp}
                className="w-full bg-[#163321] hover:bg-[#1d4a2e] text-white rounded-full h-11"
                data-testid="bonifico-open-app"
              >
                <Smartphone className="w-4 h-4 mr-2" /> Apri app della mia banca
              </Button>
              <p className="text-[10px] text-[#4A5E51] text-center mt-1.5">
                Se non funziona, scansiona il QR code sopra
              </p>
            </div>

            {/* FOOTER INFO */}
            <p className="text-[10px] text-[#4A5E51] text-center pt-2 border-t border-[#E5E2DB]">
              SEPA Instant Credit Transfer — disponibile 24/7, accredito garantito entro 10 secondi (fonte: Regolamento UE 2024/886 sui bonifici istantanei).
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
